/***
*
*   INSIGHTS
*   Internal product intelligence hub — feature adoption, revenue, and model performance
*
**********/

import { useState, useMemo } from 'react';
import { Animate, Card, Chart, Grid, Stat, Table, useAPI } from 'components/lib';
import { featureEnabled } from 'app/config';

const PRESETS = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

const EVENT_LABELS = {
  prompt_submitted: 'Prompt Submitted',
  speech_to_text_used: 'Speech to Text',
  document_upload_submitted: 'Document Upload',
  email_ai_submitted: 'Email AI',
  non_legal_submitted: 'Non-Legal',
};

const EVENT_ICONS = {
  prompt_submitted: 'message-square',
  speech_to_text_used: 'mic',
  document_upload_submitted: 'file-text',
  email_ai_submitted: 'mail',
  non_legal_submitted: 'alert-circle',
};

function toISO(date) {
  return date.toISOString().split('T')[0];
}

function buildDateParams(days) {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return `?from=${toISO(from)}&to=${toISO(to)}`;
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Shared styles
const SECTION_HEADER = {
  fontSize: 20,
  fontWeight: 700,
  marginBottom: 16,
  paddingBottom: 10,
  borderBottom: '2px solid #f3f4f6',
  color: '#111827',
};

const PLACEHOLDER_CARD = {
  background: '#f9fafb',
  border: '1px dashed #d1d5db',
  borderRadius: 12,
  padding: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 160,
  color: '#9ca3af',
  fontSize: 14,
  textAlign: 'center',
};

const PLACEHOLDER_TABLE_ROW = {
  background: '#f3f4f6',
  height: 16,
  borderRadius: 4,
  marginBottom: 10,
};

export function Insights(props) {
  const [activeDays, setActiveDays] = useState(30);
  const params = useMemo(() => buildDateParams(activeDays), [activeDays]);

  const adoption = useAPI(`/api/insights/adoption${params}`);
  const stripeEnabled = featureEnabled('stripe');
  const revenue = useAPI(stripeEnabled ? `/api/insights/revenue` : null);
  const topCustomers = useAPI(stripeEnabled ? `/api/insights/top-customers` : null);

  // Build chart data for the weekly adoption trend of prompt_submitted
  const adoptionChartData = useMemo(() => {
    const trend = adoption?.data?.weeklyTrend?.prompt_submitted;
    if (!trend || !trend.length) return null;
    return {
      labels: trend.map((d) => d.week),
      datasets: [
        {
          label: 'Prompt Submitted',
          data: trend.map((d) => d.count),
        },
      ],
    };
  }, [adoption]);

  // Build revenue trend chart data — filter out null months
  const revenueTrendData = useMemo(() => {
    const trend = revenue?.data?.trend;
    if (!trend || !trend.length) return null;
    const filtered = trend.filter((d) => d.mrr !== null);
    if (!filtered.length) return null;
    return {
      labels: filtered.map((d) => d.month),
      datasets: [
        {
          label: 'MRR',
          data: filtered.map((d) => d.mrr),
        },
      ],
    };
  }, [revenue]);

  const adoptionSummary = adoption?.data?.adoptionSummary || [];

  return (
    <Animate type='pop'>

      {/* Date presets */}
      <div className='flex items-center gap-2 mb-6'>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => setActiveDays(p.days)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeDays === p.days
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ─── SECTION 1: Product Analytics ─── */}
      <div style={SECTION_HEADER}>Product Analytics</div>

      {/* Feature Adoption */}
      <Card title='Feature Adoption'>
        <Grid cols='5'>
          {adoptionSummary.length > 0
            ? adoptionSummary.map((item) => (
                <Stat
                  key={item.name}
                  loading={adoption?.loading}
                  value={item.total}
                  label={EVENT_LABELS[item.name] || item.name}
                  icon={EVENT_ICONS[item.name] || 'activity'}
                />
              ))
            : Object.keys(EVENT_LABELS).map((name) => (
                <Stat
                  key={name}
                  loading={adoption?.loading}
                  value={undefined}
                  label={EVENT_LABELS[name]}
                  icon={EVENT_ICONS[name] || 'activity'}
                />
              ))}
        </Grid>
      </Card>

      {/* Adoption trend chart */}
      <Card title='Prompt Submissions — Weekly Trend'>
        <Chart
          type='line'
          color='blue'
          data={adoptionChartData}
          loading={adoption?.loading}
        />
      </Card>

      {/* Practice Area Breakdown */}
      <Card title='Practice Area Breakdown'>
        {/* TODO: wire practice area breakdown by module_id */}
        {/* Fetch from /api/insights/practice-areas or /api/event?group=name once endpoint exists */}
        <div style={PLACEHOLDER_CARD}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Coming Soon</div>
            <div>Prompt volume by practice area — endpoint not yet available</div>
          </div>
        </div>
      </Card>

      {/* User Retention placeholder */}
      <Card title='User Retention'>
        <Grid cols='4'>
          {/* TODO: calculate cohort retention */}
          {['Week 1', 'Week 2', 'Week 4', 'Week 8'].map((label) => (
            <Stat
              key={label}
              value='—'
              label={label}
              icon='users'
            />
          ))}
        </Grid>
        <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
          {/* TODO: calculate cohort retention */}
          Cohort retention calculation not yet implemented.
        </div>
      </Card>

      {/* ─── SECTION 2: Revenue Insights (Stripe: off in a core cell, CORE-4854) ─── */}
      {stripeEnabled && (
        <>
          <div style={{ ...SECTION_HEADER, marginTop: 40 }}>Revenue Insights</div>

          {/* Revenue stat cards */}
          <Grid cols='4'>
            <Stat
              loading={revenue?.loading}
              value={formatCurrency(revenue?.data?.mrr)}
              label='Monthly Recurring Revenue'
              icon='trending-up'
            />
            {/* TODO: Stripe ARR */}
            <Stat
              loading={revenue?.loading}
              value={formatCurrency(revenue?.data?.arr)}
              label='Annual Run Rate'
              icon='bar-chart-2'
            />
            {/* TODO: Stripe ARPU */}
            <Stat
              value='—'
              label='Avg Revenue Per User'
              icon='dollar-sign'
            />
            {/* TODO: calculate from subscription deletions */}
            <Stat
              value='—'
              label='Churn Rate'
              icon='user-minus'
            />
          </Grid>

          {/* MRR Snapshot — live data where available */}
          <Card title='MRR Snapshot'>
            <Chart
              type='line'
              color='green'
              data={revenueTrendData}
              loading={revenue?.loading}
            />
          </Card>

          {/* MRR Over Time placeholder */}
          <Card title='MRR Over Time'>
            {/* TODO: Stripe revenue data — requires Stripe Sigma or historical subscription events */}
            <div style={PLACEHOLDER_CARD}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Coming Soon</div>
                <div>Historical MRR trend — Stripe billing history not yet wired</div>
              </div>
            </div>
          </Card>

          {/* Top Customers */}
          <Card title='Top Customers by Revenue'>
            <Table
              show={['name', 'mrr', 'plan']}
              data={topCustomers?.data?.map((c) => ({
                ...c,
                mrr: formatCurrency(c.mrr),
              }))}
              loading={topCustomers?.loading}
            />
          </Card>

          {/* Recent Churned Accounts placeholder */}
          <Card title='Recent Churned Accounts'>
            {/* TODO: accounts where subscription_status = cancelled */}
            <div style={PLACEHOLDER_CARD}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Coming Soon</div>
                <div>Accounts where subscription_status = cancelled — query not yet wired</div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ─── SECTION 3: Model Performance ─── */}
      <div style={{ ...SECTION_HEADER, marginTop: 40 }}>Model Performance</div>

      {/* Model Performance stat cards */}
      <Grid cols='4'>
        {/* TODO: wire API latency metrics */}
        <Stat
          value='—'
          label='Avg Response Time'
          icon='clock'
        />
        {/* TODO: calculate from error logs */}
        <Stat
          value='—'
          label='Error Rate'
          icon='alert-triangle'
        />
        {/* TODO: count successful prompt events */}
        <Stat
          value='—'
          label='Successful Queries'
          icon='check-circle'
        />
        {/* TODO: count error events */}
        <Stat
          value='—'
          label='Failed Queries'
          icon='x-circle'
        />
      </Grid>

      {/* Response time distribution placeholder */}
      <Card title='Response Time Distribution'>
        {/* TODO: wire API latency metrics */}
        <div style={PLACEHOLDER_CARD}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Coming Soon</div>
            <div>Response time distribution — API latency logging not yet implemented</div>
          </div>
        </div>
      </Card>

      {/* Top error types placeholder */}
      <Card title='Top Error Types'>
        {/* TODO: calculate from error logs */}
        <div style={{ ...PLACEHOLDER_CARD, flexDirection: 'column', alignItems: 'stretch', minHeight: 'unset' }}>
          <div style={{ marginBottom: 12, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
            Error type aggregation not yet implemented
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} style={PLACEHOLDER_TABLE_ROW} />
          ))}
        </div>
      </Card>

    </Animate>
  );
}
